import { useCallback, useEffect, useState } from 'react';

import {
  createGoal,
  getGoals,
  getPoleOptions,
  getTypologyOptions,
} from '../../../../../../api/theHive/redeProto';
import {
  DimensionType,
  Goal,
  GoalInput,
  MetricSlug,
  PeriodType,
  PoleOption,
  Region,
  TypologyOption,
} from '../../../../../../api/theHive/redeProto/types';
import {
  REGION_ENUM_BY_LABEL,
  UF_INT_BY_SIGLA,
} from '../../../../../../constants/redeProto';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';

export interface GoalForm {
  metricSlug: MetricSlug;
  dimensionType: DimensionType;
  /** Ref da dimensão: id do polo/tipologia, sigla da UF ou label da região. */
  ref: string;
  periodType: PeriodType;
  periodNumber: string;
  year: string;
  targetValue: string;
}

const EMPTY_FORM: GoalForm = {
  metricSlug: 'credenciados_ativos',
  dimensionType: 'geral',
  ref: '',
  periodType: 'mensal',
  periodNumber: '',
  year: String(new Date().getFullYear()),
  targetValue: '',
};

interface UseGoalsHook {
  goals: Goal[];
  poleOptions: PoleOption[];
  typologyOptions: TypologyOption[];
  form: GoalForm;
  setForm: (value: GoalForm) => void;
  handleAdd: () => void;
  handleRemove: (index: number) => void;
  saving: boolean;
  loading: boolean;
  error: boolean;
}

export default function useGoals(): UseGoalsHook {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [poleOptions, setPoleOptions] = useState<PoleOption[]>([]);
  const [typologyOptions, setTypologyOptions] = useState<TypologyOption[]>([]);
  const [form, setForm] = useState<GoalForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const getData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [goalsRes, polesRes, typologiesRes] = await Promise.all([
        getGoals(),
        getPoleOptions(),
        getTypologyOptions(),
      ]);

      [goalsRes, polesRes, typologiesRes].forEach((response) => {
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        if (!response.data) {
          throw new Error('Algo deu errado, tente novamente.');
        }
      });

      setGoals(goalsRes.data ?? []);
      setPoleOptions(polesRes.data ?? []);
      setTypologyOptions(typologiesRes.data ?? []);
    } catch (requestError) {
      setError(true);
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  /** Valida o formulário conforme as invariantes do backend (evita 422). */
  const buildInput = useCallback((): GoalInput | string => {
    const year = Number(form.year);
    const targetValue = Number(form.targetValue);

    if (!Number.isFinite(year) || year < 2000 || year > 2100) {
      return 'Informe um ano entre 2000 e 2100.';
    }
    if (!Number.isFinite(targetValue) || targetValue < 0) {
      return 'Informe um valor de meta maior ou igual a 0.';
    }

    const input: GoalInput = {
      metricSlug: form.metricSlug,
      dimensionType: form.dimensionType,
      periodType: form.periodType,
      year,
      targetValue,
    };

    if (form.periodType !== 'anual') {
      const periodNumber = Number(form.periodNumber);
      const max = form.periodType === 'mensal' ? 12 : 4;
      if (
        !Number.isFinite(periodNumber) ||
        periodNumber < 1 ||
        periodNumber > max
      ) {
        return `Informe o período (1 a ${max}).`;
      }
      input.periodNumber = periodNumber;
    }

    if (form.dimensionType !== 'geral' && !form.ref) {
      return 'Selecione o alvo da dimensão.';
    }
    if (form.dimensionType === 'polo') input.poloId = Number(form.ref);
    if (form.dimensionType === 'tipologia') {
      input.typologyGroupId = Number(form.ref);
    }
    if (form.dimensionType === 'uf') input.uf = UF_INT_BY_SIGLA[form.ref];
    if (form.dimensionType === 'regiao') {
      input.regiao = REGION_ENUM_BY_LABEL[form.ref as Region];
    }

    return input;
  }, [form]);

  const handleAdd = useCallback(async () => {
    const input = buildInput();
    if (typeof input === 'string') {
      handleSnackbar(input, true);
      return;
    }
    setSaving(true);
    try {
      const response = await createGoal(input);
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      handleSnackbar('Meta salva.', false);
      setForm(EMPTY_FORM);
      await getData();
    } catch (requestError) {
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setSaving(false);
    }
  }, [buildInput, getData, handleSnackbar, getErrorMessage]);

  /** A listagem é agregada (sem id) — a remoção é apenas local. */
  const handleRemove = useCallback((index: number) => {
    setGoals((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return {
    goals,
    poleOptions,
    typologyOptions,
    form,
    setForm,
    handleAdd,
    handleRemove,
    saving,
    loading,
    error,
  };
}
